import { useState, type FormEvent } from 'react'
import { Bug, CheckCircle2, Lightbulb, MessageCircle, Send } from 'lucide-react'
import { captureError, captureEvent } from '../lib/observability'

type FeedbackCategory = 'idea' | 'problem' | 'music' | 'other'

export function Feedback() {
  const [category, setCategory] = useState<FeedbackCategory>('idea')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (message.trim().length < 8) return
    setState('sending')
    const body = new URLSearchParams({
      'form-name': 'tonaltutor-feedback',
      category,
      message: message.trim(),
      email: email.trim(),
    })
    try {
      if (!import.meta.env.DEV) {
        const response = await fetch('/', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body })
        if (!response.ok) throw new Error(`Feedback request failed: ${response.status}`)
      }
      setState('sent')
      captureEvent('feedback_submitted', { category, hasReplyEmail: Boolean(email.trim()) })
    } catch (error) {
      setState('error')
      captureError(error, { surface: 'feedback' })
    }
  }

  if (state === 'sent') {
    return (
      <main id="main-content" className="page-shell narrow-page">
        <div className="feedback-success"><CheckCircle2 aria-hidden="true" /><h1>Feedback received</h1><p>Thank you. Your message has been submitted.</p></div>
      </main>
    )
  }

  return (
    <main id="main-content" className="page-shell narrow-page">
      <section className="page-hero compact">
        <div><h1>Feedback</h1></div>
        <p>Report a musical issue, technical problem, or exercise suggestion.</p>
      </section>

      <form className="feedback-form" name="tonaltutor-feedback" onSubmit={(event) => void submit(event)}>
        <fieldset>
          <legend>What kind of feedback is this?</legend>
          <div className="feedback-categories">
            <button className={category === 'idea' ? 'feedback-category selected' : 'feedback-category'} type="button" onClick={() => setCategory('idea')}><Lightbulb aria-hidden="true" /><strong>Feature idea</strong></button>
            <button className={category === 'problem' ? 'feedback-category selected' : 'feedback-category'} type="button" onClick={() => setCategory('problem')}><Bug aria-hidden="true" /><strong>Something broke</strong></button>
            <button className={category === 'music' ? 'feedback-category selected' : 'feedback-category'} type="button" onClick={() => setCategory('music')}><MessageCircle aria-hidden="true" /><strong>Music accuracy</strong></button>
            <button className={category === 'other' ? 'feedback-category selected' : 'feedback-category'} type="button" onClick={() => setCategory('other')}><MessageCircle aria-hidden="true" /><strong>Something else</strong></button>
          </div>
        </fieldset>

        <label className="field-label">Your message<textarea value={message} onChange={(event) => setMessage(event.target.value)} minLength={8} maxLength={2000} required placeholder="Tell us what happened, what you expected, or what would make training more useful…" /><small>{message.length} / 2000</small></label>
        <label className="field-label">Email for a reply <span>(optional)</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="you@example.com" /><small>Used only to reply to this message.</small></label>
        {state === 'error' && <p className="form-error" role="alert">The message could not be sent. Please try again shortly.</p>}
        <button className="button button-primary button-large" type="submit" disabled={state === 'sending' || message.trim().length < 8}><Send aria-hidden="true" />{state === 'sending' ? 'Sending…' : 'Send feedback'}</button>
      </form>
    </main>
  )
}
